/**
 * PATH: packages/ui/src/components/email/useFitToWidth.ts
 * FILE_ID: UI-EMAIL-FITTOWIDTH-001
 * PURPOSE: Shrink an email body just enough to FIT its reader column when
 *          its content is wider than the column and cannot reflow.
 * WHY:     Operator (recurring): "this email did not fit the frame". The
 *          canonical renderer (EmailHtmlBody) already caps tables/images to
 *          `max-w-full` and lets reflowable emails wrap, with `overflow-x-auto`
 *          as the fallback. But a NON-reflowable wide email — a fixed-width
 *          banner image, fixed-px table cells, a long unbreakable token — still
 *          exceeds the column and the fallback merely SCROLLS it (the right
 *          edge sits off-frame). This hook measures the body's natural width
 *          against the column and applies a CSS `zoom` so it scales down to fit
 *          instead of scrolling — the same property EmailHtmlBody already uses
 *          for manual zoom, so the content REFLOWS within the column.
 * FLOW:
 *   ┌───────────────┐   ┌──────────────────────┐   ┌────────────────────┐
 *   │ container W +  │ → │ computeFitZoom(...)   │ → │ CSS zoom on body   │
 *   │ content natW   │   │ clamp(W/natW, floor,1)│   │ (≤1, only on need) │
 *   └───────────────┘   └──────────────────────┘   └────────────────────┘
 * RELATED:
 *   - EmailHtmlBody.tsx (UI-EMAIL-HTMLBODY-001) — the single consumer.
 */
import { useLayoutEffect, useRef, useState, type RefObject } from 'react'

/**
 * Pure: the zoom factor that makes `naturalWidth` fit inside `containerWidth`.
 * Never scales UP (caps at 1) and never below `floor` (so text stays legible;
 * EmailHtmlBody's `overflow-x-auto` remains the final safety net for the
 * pathological email that is still wider than the column at the floor).
 *
 * Inputs that can't yield a meaningful ratio (missing/zero widths, or content
 * already narrower than the column) return 1 — i.e. "do nothing", so the
 * common case is byte-identical to no zoom.
 */
export function computeFitZoom(
  naturalWidth: number,
  containerWidth: number,
  floor = 0.5,
): number {
  if (
    !Number.isFinite(naturalWidth) ||
    !Number.isFinite(containerWidth) ||
    naturalWidth <= 0 ||
    containerWidth <= 0 ||
    naturalWidth <= containerWidth
  ) {
    return 1
  }
  const ratio = containerWidth / naturalWidth
  if (ratio >= 1) return 1
  return Math.max(floor, ratio)
}

/**
 * Width of a classic (non-overlay) scrollbar, in CSS px, with margin. The fit
 * loop guard (`shouldRefit`) treats any column-width INCREASE smaller than this
 * as "the loop's own echo" rather than a real resize.
 */
const SCROLLBAR_EPS = 24

export interface FitInputs {
  /** The clipping column's inner width (`clientWidth`) — the budget to fit. */
  width: number
  /** The content's natural (zoom-1) width — `scrollWidth` read at zoom 1. */
  natural: number
}

/**
 * Decide whether a freshly-measured (width, natural) pair should trigger a
 * re-fit, given the inputs the CURRENTLY-applied zoom was computed from.
 *
 * BUG-2026-06-27-EMAIL-BODY-FLICKER-SCROLLBAR-LOOP — operator: "the body in
 * the email is flickering" (continuous, on a thread that ~fills the reader
 * pane). Root cause: a self-induced ResizeObserver feedback loop. Applying the
 * fit `zoom` makes the body (and so the whole thread) SHORTER, which lets the
 * reader's `overflow-y-auto` scroll pane drop its vertical scrollbar; that
 * hands back ~one scrollbar-width of inner width, so the observed column
 * `clientWidth` GROWS ~15px → the observer re-fires → we fit to the wider
 * column → the body grows taller → the scrollbar reappears → the column shrinks
 * 15px → … forever. (BUG-2026-06-25's synchronous first measure fixed the
 * first-paint snap, not this loop — the observer still reacts to a size change
 * the fit itself caused.)
 *
 * Invariant: the fit may only recompute on a REAL input change —
 *   • the column NARROWING (content would overflow → must shrink; always
 *     honoured for safety),
 *   • the column WIDENING by more than a scrollbar (a genuine window/pane
 *     resize), or
 *   • the content's NATURAL width changing (new body, late-loading wide image).
 * A column widening by LESS than a scrollbar is the loop's own echo and is
 * ignored, so the stable state is "fitted to the narrower budget" — no
 * overflow, no bounce.
 */
export function shouldRefit(
  prev: FitInputs | null,
  next: FitInputs,
  scrollbarEps: number = SCROLLBAR_EPS,
): boolean {
  if (!prev) return true // first measure for this content/column
  if (Math.abs(next.natural - prev.natural) > 1) return true // content changed
  if (next.width < prev.width - 1) return true // column narrowed → must shrink
  if (next.width > prev.width + scrollbarEps) return true // genuine widen
  return false // sub-scrollbar widen → the loop's own echo, ignore
}

export interface UseFitToWidthOptions {
  /** When false the hook is inert and always returns 1 (manual-zoom / opt-out
   *  paths keep the exact previous behaviour). */
  enabled: boolean
  /** The clipping column — its `clientWidth` is the budget to fit into. */
  containerRef: RefObject<HTMLElement | null>
  /** The scaled body node — its `scrollWidth` (read at zoom 1) is the natural
   *  content width. */
  contentRef: RefObject<HTMLElement | null>
  /** Minimum scale before we give up and let the body scroll. */
  floor?: number
  /** Re-measure when this changes (e.g. the rendered html/text). */
  signal?: unknown
}

/**
 * Returns the fit zoom (≤1) for the measured body, re-measuring on mount,
 * content change, and column resize. Returns 1 whenever disabled or when the
 * content already fits, so a render that doesn't overflow is unaffected.
 */
export function useFitToWidth({
  enabled,
  containerRef,
  contentRef,
  floor = 0.5,
  signal,
}: UseFitToWidthOptions): number {
  const [zoom, setZoom] = useState(1)
  // The (width, natural) inputs the currently-applied zoom was computed from —
  // the fit loop guard (`shouldRefit`) compares each new measurement against
  // this so a scrollbar-toggle width wiggle the fit itself caused can't re-fit.
  const lastInputsRef = useRef<FitInputs | null>(null)

  useLayoutEffect(() => {
    if (!enabled) {
      setZoom(1)
      lastInputsRef.current = null
      return
    }
    const container = containerRef.current
    const content = contentRef.current
    if (!container || !content) return

    // New content / column / floor (the effect deps): forget the previous fit
    // baseline so the next measure fits this body from scratch.
    lastInputsRef.current = null

    let frame = 0
    const measure = () => {
      const client = container.clientWidth
      if (!client) return
      // Read the natural width at zoom 1 so the ratio is independent of the
      // zoom we may already have applied (CSS `zoom` scales `scrollWidth`).
      // Mutating the style imperatively here is safe: it happens inside a
      // layout effect, before paint, and the next render re-applies the
      // committed zoom from state.
      const prev = content.style.zoom
      content.style.zoom = '1'
      const natural = content.scrollWidth
      content.style.zoom = prev
      const nextInputs: FitInputs = { width: client, natural }
      // LOOP GUARD (BUG-2026-06-27-EMAIL-BODY-FLICKER-SCROLLBAR-LOOP): a
      // column-width wiggle the fit itself induced — the reader's
      // `overflow-y-auto` pane toggling its vertical scrollbar as the zoomed
      // body changes height — must NOT re-fit, or it bounces forever.
      if (!shouldRefit(lastInputsRef.current, nextInputs)) return
      lastInputsRef.current = nextInputs
      const next = computeFitZoom(natural, client, floor)
      setZoom((current) => (Math.abs(current - next) > 0.005 ? next : current))
    }
    const schedule = () => {
      if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(frame)
      frame =
        typeof requestAnimationFrame !== 'undefined'
          ? requestAnimationFrame(measure)
          : (measure(), 0)
    }

    // BUG-2026-06-25-EMAIL-BODY-FLICKER-ON-OPEN — operator: "the email
    // flickers a bit" (once or twice when opening an email). Root cause:
    // the FIRST fit-to-width measure was deferred to the next frame via
    // ``requestAnimationFrame``, so an overflowing body PAINTED at zoom 1
    // and then visibly snapped down to its fit-scale one frame later — a
    // perceptible rescale jump on every wide email.
    //
    // Measure synchronously here instead. We're already inside a layout
    // effect (DOM committed + laid out, ``scrollWidth`` accurate, before
    // paint), so ``setZoom`` re-renders + re-commits in the SAME frame and
    // the browser only ever paints the final, fitted body. The rAF
    // ``schedule`` is retained ONLY for subsequent re-measures driven by
    // the ResizeObserver (column resize, late image reflow), where
    // coalescing a burst of callbacks into one measure-per-frame is the
    // point — and where a corrected zoom is a deliberate response to a
    // real size change, not a first-paint artefact.
    measure()

    if (typeof ResizeObserver === 'undefined') {
      return () => {
        if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(frame)
      }
    }
    const ro = new ResizeObserver(schedule)
    ro.observe(container)
    return () => {
      if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(frame)
      ro.disconnect()
    }
  }, [enabled, containerRef, contentRef, floor, signal])

  return enabled ? zoom : 1
}
